import { useRef, useEffect, useCallback } from 'react';
import { useMetronomeStore } from '../pages/View/components/plugins/MetronomePlugin/MetronomePlugin';
import { prepareClickSound } from '../utils/clickSounds';
import { scheduleClickSound } from '../utils/metronomeClick';
import { collectClicks, createCursor } from '../utils/metronomeSchedule';

// How far ahead (seconds) to schedule audio events into the Web Audio
// timeline. Beats are pre-scheduled for sample-accurate playback.
const LOOK_AHEAD = 0.1;

const parseTimeSignature = (timeSignature) => {
  if (!timeSignature) return 4;

  const [numerator] = timeSignature.split('/');

  return parseInt(numerator) || 4;
};

/**
 * Core metronome engine with look-ahead scheduling via the Web Audio API.
 *
 * A Web Worker wakes up every ~25ms and the main thread pre-schedules beats
 * into the audio timeline for sample-accurate, drift-free playback.
 *
 * @param {string} id - Unique identifier for this metronome instance.
 * @param {object} options
 * @param {function} options.onStarted  - Called when the worker confirms start.
 * @param {function} options.onStopped  - Called when the worker confirms stop.
 * @param {function} options.onFlash    - Called when the UI should flash for a beat.
 * @param {function} options.onFlashEnd - Called when the flash should end (~100ms later).
 */
const useMetronomeEngine = (id, { onStarted, onStopped, onFlash, onFlashEnd } = {}) => {
  const workerRef = useRef(null);
  const audioContextRef = useRef(null);
  const flashTimeoutRef = useRef(null);

  // Look-ahead scheduler state
  const cursorRef = useRef(createCursor(0));
  const bpmRef = useRef(0);
  const beatsPerMeasureRef = useRef(4);
  const isSchedulingRef = useRef(false);

  // Keep callback refs stable so the scheduler doesn't re-create on every render
  const onFlashRef = useRef(onFlash);
  const onFlashEndRef = useRef(onFlashEnd);
  const onStartedRef = useRef(onStarted);
  const onStoppedRef = useRef(onStopped);
  onFlashRef.current = onFlash;
  onFlashEndRef.current = onFlashEnd;
  onStartedRef.current = onStarted;
  onStoppedRef.current = onStopped;

  const setActiveMetronome = useMetronomeStore((s) => s.setActiveMetronome);
  const stopMetronomeStore = useMetronomeStore((s) => s.stopMetronome);
  const setGlobalIsFlashing = useMetronomeStore((s) => s.setIsFlashing);
  const activeMetronomeId = useMetronomeStore((s) => s.activeMetronomeId);
  // Rendered ahead of the first beat, so it plays from a buffer rather than live. Subscribed rather
  // than selected, so a sound change doesn't re-render the engine.
  useEffect(() => {
    prepareClickSound(useMetronomeStore.getState().soundType);

    return useMetronomeStore.subscribe((state, previous) => {
      if (state.soundType !== previous.soundType) prepareClickSound(state.soundType);
    });
  }, []);

  const createAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }

    return audioContextRef.current;
  }, []);

  // Schedule a click at an exact Web Audio time (sample-accurate)
  const scheduleClick = useCallback((time, variant) => {
    const audioContext = audioContextRef.current;
    if (!audioContext) return;

    const { volume, soundType: currentSoundType } = useMetronomeStore.getState();

    scheduleClickSound(audioContext, time, variant, { soundType: currentSoundType, volume });
  }, []);

  // Look-ahead scheduler: pre-schedule beats, and any subdivision clicks between them, into the
  // Web Audio timeline
  const scheduler = useCallback(() => {
    const audioContext = audioContextRef.current;
    const bpm = bpmRef.current;
    const isScheduling = isSchedulingRef.current;

    if (!audioContext || !bpm || !isScheduling) return;

    const { accentBeat, subdivision } = useMetronomeStore.getState();
    const { cursor, clicks } = collectClicks(cursorRef.current, {
      now: audioContext.currentTime,
      lookAhead: LOOK_AHEAD,
      bpm,
      beatsPerMeasure: beatsPerMeasureRef.current,
      subdivision,
    });

    cursorRef.current = cursor;

    clicks.forEach(({ time, beat, step }) => {
      if (step > 0) {
        scheduleClick(time, 'subdivision');

        return;
      }

      scheduleClick(time, accentBeat && beat === 1 ? 'downbeat' : 'regular');

      // Schedule UI flash to fire when the beat actually plays
      const delay = Math.max(0, (time - audioContext.currentTime) * 1000);
      setTimeout(() => {
        if (!isSchedulingRef.current) return;

        setGlobalIsFlashing(true);
        onFlashRef.current?.();

        if (flashTimeoutRef.current) clearTimeout(flashTimeoutRef.current);

        flashTimeoutRef.current = setTimeout(() => {
          setGlobalIsFlashing(false);
          onFlashEndRef.current?.();
        }, 100);
      }, delay);
    });
  }, [scheduleClick, setGlobalIsFlashing]);

  // Initialize Web Worker
  useEffect(() => {
    workerRef.current = new Worker('/metronome-worker.js');

    workerRef.current.onmessage = (e) => {
      const { type } = e.data;
      switch (type) {
        case 'schedulerTick':
          scheduler();
          break;
        case 'updateBPM':
          bpmRef.current = e.data.bpm;
          beatsPerMeasureRef.current = parseTimeSignature(e.data.timeSignature);
          break;
        case 'started':
          onStartedRef.current?.();
          break;
        case 'stopped':
          isSchedulingRef.current = false;
          setGlobalIsFlashing(false);
          onStoppedRef.current?.();
          // Suspend so Safari drops the "playing" tab icon, unless this engine already restarted
          if (useMetronomeStore.getState().activeMetronomeId !== id) audioContextRef.current?.suspend();
          break;
        default:
          break;
      }
    };

    return () => {
      if (workerRef.current) {
        workerRef.current.postMessage({ action: 'stop' });
        workerRef.current.terminate();
      }
      isSchedulingRef.current = false;
      audioContextRef.current?.close();
      audioContextRef.current = null;

      if (flashTimeoutRef.current) clearTimeout(flashTimeoutRef.current);
      stopMetronomeStore(id);
    };
    // id is stable (comes from a ref in the consumer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduler, stopMetronomeStore, setGlobalIsFlashing]);

  // Yield if another metronome becomes globally active
  const yieldToOther = useCallback(() => {
    isSchedulingRef.current = false;
    workerRef.current?.postMessage({ action: 'stop' });
  }, []);

  const start = useCallback(
    (bpm, timeSignature = '4/4') => {
      if (!bpm || bpm <= 0) return;

      setActiveMetronome(id, workerRef.current, bpm);

      const ctx = createAudioContext();

      if (ctx.state === 'suspended') ctx.resume();

      // Initialize look-ahead scheduler state
      bpmRef.current = bpm;
      beatsPerMeasureRef.current = parseTimeSignature(timeSignature);
      cursorRef.current = createCursor(ctx.currentTime);
      isSchedulingRef.current = true;

      workerRef.current.postMessage({ action: 'start', bpm, timeSignature });
    },
    [id, setActiveMetronome, createAudioContext],
  );

  const stop = useCallback(() => {
    isSchedulingRef.current = false;
    workerRef.current?.postMessage({ action: 'stop' });
    stopMetronomeStore(id);
  }, [id, stopMetronomeStore]);

  const updateBPM = useCallback((bpm, timeSignature = '4/4') => {
    if (!bpm || bpm <= 0) return;

    bpmRef.current = bpm;
    beatsPerMeasureRef.current = parseTimeSignature(timeSignature);

    if (workerRef.current) {
      workerRef.current.postMessage({ action: 'updateBPM', bpm, timeSignature });
    }
  }, []);

  // Create and resume the AudioContext without scheduling any beats.
  // Useful for warming up the context so the first start() is consistent.
  const prime = useCallback(() => {
    const ctx = createAudioContext();
    if (ctx.state === 'suspended') ctx.resume();
  }, [createAudioContext]);

  return {
    start,
    stop,
    updateBPM,
    yieldToOther,
    prime,
    workerRef,
    activeMetronomeId,
    id,
  };
};

export default useMetronomeEngine;
